import { CheckCircleIcon, InfoIcon, WarningCircleIcon, WarningIcon, XIcon } from '@phosphor-icons/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import AxiosInterceptor from 'api/interceptor/axiosInterceptor';
import { router } from 'AppRouter';
import { ErrorBoundary } from 'components/ErrorBoundary/ErrorBoundary';
import { SnackbarTransition } from 'components/Snackbar/SnackbarTransition';
import { PanelManager } from 'context/PanelManager';
import { useBlockUnload } from 'hooks/useBlockUnload';
import { SnackbarProvider } from 'notistack';
import type { FC, PropsWithChildren } from 'react';
import { useRef } from 'react';
import { Provider } from 'react-redux';
import { RouterProvider } from 'react-router';
import { store } from 'redux/store';
import { IconButton } from 'ui-library/components/IconButton/IconButton';

const client = new QueryClient();

const Snackbar: FC<PropsWithChildren> = ({ children }) => {
  'use memo';
  const notistackRef = useRef<SnackbarProvider>(null);

  return (
    <SnackbarProvider
      ref={notistackRef}
      autoHideDuration={5000}
      maxSnack={5}
      anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
      TransitionComponent={SnackbarTransition}
      iconVariant={{
        success: <CheckCircleIcon weight="fill" size={16} />,
        info: <InfoIcon weight="fill" size={16} />,
        warning: <WarningCircleIcon weight="fill" size={16} />,
        error: <WarningIcon weight="fill" size={16} />,
      }}
      action={key => (
        <IconButton
          variant="clear"
          size="xs"
          aria-label="Dismiss notification"
          onClick={() => notistackRef.current?.closeSnackbar(key)}
        >
          <XIcon size={12} weight="bold" />
        </IconButton>
      )}
    >
      {children}
    </SnackbarProvider>
  );
};

export const App = () => {
  'use memo';
  useBlockUnload(client);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={client}>
        <Provider store={store}>
          <Snackbar>
            <AxiosInterceptor>
              <PanelManager>
                <RouterProvider router={router} />
              </PanelManager>
            </AxiosInterceptor>
          </Snackbar>
        </Provider>
        <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
      </QueryClientProvider>
    </ErrorBoundary>
  );
};
